import {useMemo} from "react";

function computeFromConfig(cfg) {
  const {
    workforce,
    licensing,
    pilot_setup_fees,
    timeline,
    baselines,
    assumptions,
    organization,
  } = cfg;
  const {number_of_med_reps_for_pilot, number_of_medical_representatives} =
    workforce;
  const {
    licence_for_small_team,
    avatar_package,
    avatar_package_minutes,
    avatar_minutes_per_user,
    licence_for_medium_team,
  } = licensing;
  const {number_of_months, implementation_timeline_months} = timeline;
  const {rollout} = cfg;
  const {platform_setup_and_configuration} = rollout;
  const {preparation_and_recommendation, tech_setup, product_setup} =
    pilot_setup_fees;
  const {
    current_onboarding_cost_per_rep,
    manager_hourly_rate,
    onboarding_training_time_required_hours_rep,
    rep_hourly_cost,
    average_territory_sales,
  } = baselines;
  const {
    current_annual_cost_total_organization,
    efficiency_gain_ratio,
    estimated_penalties_prevented,
  } = organization ?? {};

  const {
    onboarding_cost_reduction_percent,
    newcomers_share_percent,
    manager_time_freed_hours_per_month_per_manager,
    manager_span_of_control,
    onboarding_time_reduction_fresh_newcomers_percent,
    onboarding_time_reduction_experienced_newcomers_percent,
    percentage_of_fresh_newcomers,
    percentage_of_experienced_newcomers,
    estimated_sales_uplift_percent,
  } = assumptions;

  if (number_of_medical_representatives === 0) {
    return {
      SUBTOTAL_PILOT: 0,
      ROLLOUT_PLATFORM_FIXED: 0,
      ROLLOUT_RECURRING_LICENSES_AND_AVATAR: 0,
      SUBTOTAL_ROLLOUT: 0,
      TOTAL_YEAR_1_INVESTMENT: 0,
      ESTIMATED_ANNUAL_SAVINGS: 0,
      ESTIMATED_VALUE_OF_FREED_TIME: 0,
      ESTIMATED_ANNUAL_EFFICIENCY_VALUE: 0,
      SUBTOTAL_COST_SAVINGS: 0,
      PRODUCTIVITY_VALUE_OF_FRESH_NEWCOMERS: 0,
      PRODUCTIVITY_VALUE_OF_EXPERIENCED_NEWCOMERS: 0,
      ESTIMATED_PRODUCTIVITY_VALUE: 0,
      ESTIMATED_GROSS_PROFIT_IMPACT: 0,
      ESTIMATED_PENALTIES_PREVENTED: 0,
      SUBTOTAL_REVENUE_RISK_MITIGATION: 0,
      TOTAL_ESTIMATED_BENEFITS_YEAR_1: 0,
      NET_BENEFIT: 0,
      ROI: 0,
      TOTAL_INVESTMENT_CONSERVATIVE: 0,
      TOTAL_INVESTMENT_REVISED: 0,
      TOTAL_INVESTMENT_OPTIMISTIC: 0,
      TOTAL_BENEFITS_CONSERVATIVE: 0,
      TOTAL_BENEFITS_REVISED: 0,
      TOTAL_BENEFITS_OPTIMISTIC: 0,
      NET_BENEFIT_CONSERVATIVE: 0,
      NET_BENEFIT_REVISED: 0,
      NET_BENEFIT_OPTIMISTIC: 0,
      ROI_CONSERVATIVE: 0,
      ROI_REVISED: 0,
      ROI_OPTIMISTIC: 0,
      PAYBACK_MONTHS_CONSERVATIVE: 0,
      PAYBACK_MONTHS_REVISED: 0,
      PAYBACK_MONTHS_OPTIMISTIC: 0,
    };
  }

  const dynamicNumberOfNewcomers = Math.round(
    number_of_medical_representatives * (newcomers_share_percent / 100),
  );
  const dynamicNumberOfSalesManagers = Math.round(
    number_of_medical_representatives / manager_span_of_control,
  );
  const getAvatarPackagesCount = (usersCount) =>
    Math.ceil((usersCount * avatar_minutes_per_user) / avatar_package_minutes);

  const SUBTOTAL_PILOT =
    preparation_and_recommendation +
    tech_setup +
    product_setup +
    number_of_med_reps_for_pilot * licence_for_small_team +
    avatar_package * getAvatarPackagesCount(number_of_med_reps_for_pilot);

  const ROLLOUT_PLATFORM_FIXED = platform_setup_and_configuration;

  const ROLLOUT_RECURRING_LICENSES_AND_AVATAR =
    number_of_medical_representatives *
      licence_for_medium_team *
      number_of_months +
    avatar_package *
      getAvatarPackagesCount(number_of_medical_representatives) *
      number_of_months;

  const SUBTOTAL_ROLLOUT =
    ROLLOUT_PLATFORM_FIXED + ROLLOUT_RECURRING_LICENSES_AND_AVATAR;

  const TOTAL_YEAR_1_INVESTMENT = SUBTOTAL_PILOT + SUBTOTAL_ROLLOUT;

  const ESTIMATED_ANNUAL_SAVINGS =
    ((current_onboarding_cost_per_rep * onboarding_cost_reduction_percent) /
      100) *
    dynamicNumberOfNewcomers;

  const ESTIMATED_VALUE_OF_FREED_TIME =
    manager_time_freed_hours_per_month_per_manager *
    dynamicNumberOfSalesManagers *
    implementation_timeline_months *
    manager_hourly_rate;

  /* Лист «Benefits» п.3: current annual cost (total org) × efficiency gain % */
  const ESTIMATED_ANNUAL_EFFICIENCY_VALUE =
    (current_annual_cost_total_organization ?? 0) *
    (efficiency_gain_ratio ?? 0);

  const SUBTOTAL_COST_SAVINGS =
    ESTIMATED_ANNUAL_SAVINGS +
    ESTIMATED_VALUE_OF_FREED_TIME +
    ESTIMATED_ANNUAL_EFFICIENCY_VALUE;

  const freshReduction =
    onboarding_time_reduction_fresh_newcomers_percent / 100;
  const experiencedReduction =
    onboarding_time_reduction_experienced_newcomers_percent / 100;
  const freshShare = percentage_of_fresh_newcomers / 100;
  const experiencedShare = percentage_of_experienced_newcomers / 100;

  const PRODUCTIVITY_VALUE_OF_FRESH_NEWCOMERS =
    dynamicNumberOfNewcomers *
    freshShare *
    onboarding_training_time_required_hours_rep *
    freshReduction *
    rep_hourly_cost;

  const PRODUCTIVITY_VALUE_OF_EXPERIENCED_NEWCOMERS =
    dynamicNumberOfNewcomers *
    experiencedShare *
    onboarding_training_time_required_hours_rep *
    experiencedReduction *
    rep_hourly_cost;

  const ESTIMATED_PRODUCTIVITY_VALUE =
    PRODUCTIVITY_VALUE_OF_FRESH_NEWCOMERS +
    PRODUCTIVITY_VALUE_OF_EXPERIENCED_NEWCOMERS;

  const ESTIMATED_GROSS_PROFIT_IMPACT =
    average_territory_sales *
    (estimated_sales_uplift_percent / 100) *
    number_of_medical_representatives;

  const SUBTOTAL_REVENUE_RISK_MITIGATION =
    ESTIMATED_PRODUCTIVITY_VALUE +
    ESTIMATED_GROSS_PROFIT_IMPACT +
    estimated_penalties_prevented;

  const TOTAL_ESTIMATED_BENEFITS_YEAR_1 =
    SUBTOTAL_COST_SAVINGS + SUBTOTAL_REVENUE_RISK_MITIGATION;

  const NET_BENEFIT = TOTAL_ESTIMATED_BENEFITS_YEAR_1 - TOTAL_YEAR_1_INVESTMENT;

  const ROI =
    TOTAL_YEAR_1_INVESTMENT > 0
      ? (NET_BENEFIT / TOTAL_YEAR_1_INVESTMENT) * 100
      : 0;

  const TOTAL_INVESTMENT_CONSERVATIVE = TOTAL_YEAR_1_INVESTMENT;
  const TOTAL_INVESTMENT_REVISED = TOTAL_YEAR_1_INVESTMENT;
  const TOTAL_INVESTMENT_OPTIMISTIC = TOTAL_YEAR_1_INVESTMENT;

  const TOTAL_BENEFITS_CONSERVATIVE =
    TOTAL_ESTIMATED_BENEFITS_YEAR_1 - TOTAL_ESTIMATED_BENEFITS_YEAR_1 * 0.3;
  const TOTAL_BENEFITS_REVISED = TOTAL_ESTIMATED_BENEFITS_YEAR_1;
  const TOTAL_BENEFITS_OPTIMISTIC =
    TOTAL_ESTIMATED_BENEFITS_YEAR_1 + TOTAL_ESTIMATED_BENEFITS_YEAR_1 * 0.3;

  const NET_BENEFIT_CONSERVATIVE =
    TOTAL_BENEFITS_CONSERVATIVE - TOTAL_INVESTMENT_CONSERVATIVE;
  const NET_BENEFIT_REVISED = TOTAL_BENEFITS_REVISED - TOTAL_INVESTMENT_REVISED;
  const NET_BENEFIT_OPTIMISTIC =
    TOTAL_BENEFITS_OPTIMISTIC - TOTAL_INVESTMENT_OPTIMISTIC;

  const ROI_CONSERVATIVE =
    TOTAL_INVESTMENT_CONSERVATIVE > 0
      ? (NET_BENEFIT_CONSERVATIVE / TOTAL_INVESTMENT_CONSERVATIVE) * 100
      : 0;
  const ROI_REVISED =
    TOTAL_INVESTMENT_REVISED > 0
      ? (NET_BENEFIT_REVISED / TOTAL_INVESTMENT_REVISED) * 100
      : 0;
  const ROI_OPTIMISTIC =
    TOTAL_INVESTMENT_OPTIMISTIC > 0
      ? (NET_BENEFIT_OPTIMISTIC / TOTAL_INVESTMENT_OPTIMISTIC) * 100
      : 0;

  const PAYBACK_MONTHS_CONSERVATIVE =
    TOTAL_BENEFITS_CONSERVATIVE > 0
      ? (TOTAL_INVESTMENT_CONSERVATIVE / TOTAL_BENEFITS_CONSERVATIVE) * 12
      : 0;
  const PAYBACK_MONTHS_REVISED =
    TOTAL_BENEFITS_REVISED > 0
      ? (TOTAL_INVESTMENT_REVISED / TOTAL_BENEFITS_REVISED) * 12
      : 0;
  const PAYBACK_MONTHS_OPTIMISTIC =
    TOTAL_BENEFITS_OPTIMISTIC > 0
      ? (TOTAL_INVESTMENT_OPTIMISTIC / TOTAL_BENEFITS_OPTIMISTIC) * 12
      : 0;

  return {
    SUBTOTAL_PILOT,
    ROLLOUT_PLATFORM_FIXED,
    ROLLOUT_RECURRING_LICENSES_AND_AVATAR,
    SUBTOTAL_ROLLOUT,
    TOTAL_YEAR_1_INVESTMENT,
    ESTIMATED_ANNUAL_SAVINGS,
    ESTIMATED_VALUE_OF_FREED_TIME,
    ESTIMATED_ANNUAL_EFFICIENCY_VALUE,
    SUBTOTAL_COST_SAVINGS,
    PRODUCTIVITY_VALUE_OF_FRESH_NEWCOMERS,
    PRODUCTIVITY_VALUE_OF_EXPERIENCED_NEWCOMERS,
    ESTIMATED_PRODUCTIVITY_VALUE,
    ESTIMATED_GROSS_PROFIT_IMPACT,
    ESTIMATED_PENALTIES_PREVENTED: estimated_penalties_prevented ?? 0,
    SUBTOTAL_REVENUE_RISK_MITIGATION,
    TOTAL_ESTIMATED_BENEFITS_YEAR_1,
    NET_BENEFIT,
    ROI,
    TOTAL_INVESTMENT_CONSERVATIVE,
    TOTAL_INVESTMENT_REVISED,
    TOTAL_INVESTMENT_OPTIMISTIC,
    TOTAL_BENEFITS_CONSERVATIVE,
    TOTAL_BENEFITS_REVISED,
    TOTAL_BENEFITS_OPTIMISTIC,
    NET_BENEFIT_CONSERVATIVE,
    NET_BENEFIT_REVISED,
    NET_BENEFIT_OPTIMISTIC,
    ROI_CONSERVATIVE,
    ROI_REVISED,
    ROI_OPTIMISTIC,
    PAYBACK_MONTHS_CONSERVATIVE,
    PAYBACK_MONTHS_REVISED,
    PAYBACK_MONTHS_OPTIMISTIC,
  };
}

export function useEffectiveness(config) {
  return useMemo(() => computeFromConfig(config), [config]);
}
